import React, { useCallback, useState, useContext } from 'react';
import { Layout, message } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { TTBPageHeader } from '../../organisms/ttb/TTBPageHeader';
import { TTBTable } from '../../organisms/ttb/TTBTable';
import { FilterTTBType, TTBType } from '../../../types/tryThenBuy';
import { getTTBListRepository } from '../../../repository/mytm/try-then-buy/ttbRepository';
import { PAGE_SIZE_OPTIONS, PER_PAGE_DEFAULT } from '../../../constants/common';
import { API_URLS } from '../../../constants/urls/api-urls';
import { TTBFilter } from '../../organisms/ttb/TTBFilter';
import useDebounce from '../../../hooks/debounced';
import { getProductImageUrlFromSelectNodes, getProductNameFromHeadModel } from '../../../utils/transfer/ttb';
import { appStyles } from '../../../styles';
import { UserContext } from '../../../repository/context/userContext';
import { Role } from '../../../constants/define/role';
import { TRY_THEN_BUY } from '../../../constants/define/translation';

const { Content } = Layout;

const filterDefault = {
  strSearch: '',
  status: [],
  abnormalOrder: undefined,
};

export const TryThenBuy = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_DEFAULT);
  const [loading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<TTBType[]>([]);
  const [filterData, setFilterData] = useState<FilterTTBType>(filterDefault);
  const debouncedSearch = useDebounce(filterData.strSearch, 500);
  const { t } = useTranslation(TRY_THEN_BUY);

  const userContext = useContext(UserContext);
  const info: any = userContext.user;
  const isRoleTTBAgent = [Role.TTB_AGENT].includes(info?.role);
  const isRoleTTBManager = [Role.TTB_MANAGER].includes(info?.role);
  const isRoleAdmin = [Role.ADMIN, Role.SUPER_ADMIN].includes(info?.role);
  const handleChangePage = useCallback((pg: number, pgSize?: number) => {
    setPage(pg);
    setPageSize(pgSize || PER_PAGE_DEFAULT);
  }, []);

  const onShowSizeChange = useCallback((current: number, size: number) => {
    setPage(current);
    setPageSize(size || PER_PAGE_DEFAULT);
  }, []);

  const { isValidating, error, mutate } = useSWR(
    [API_URLS.TRY_THEN_BUY_LIST, page, pageSize, filterData.status, filterData.abnormalOrder, debouncedSearch],
    (url: string, pageNumber: number, pageSizeNumber: number, status: any, abnormalOrder: boolean, strSearch: any) =>
      getTTBListRepository(
        url,
        {
          status: status.join(','),
          page: pageNumber,
          take: pageSizeNumber || PER_PAGE_DEFAULT,
          strSearch: strSearch.trim(),
          abnormalOrder: abnormalOrder ? 1 : 0,
        },
        isRoleTTBAgent,
      ).then(dataOrder => {
        if (dataOrder?.total === 0 && debouncedSearch) {
          message.destroy();
          message.error(t('message.orderNotFound', { orderId: debouncedSearch }));
        }
        setTotal(dataOrder.total);
        const recordTTB = dataOrder.orders.map((item: TTBType, index: number): any => ({
          ...item,
          index,
          key: index,
          price: item?.productInfo?.price,
          productType: item?.productInfo?.type,
          productId: item?.productInfo?.productId,
          productName: getProductNameFromHeadModel(item?.productInfo?.cTmCustomconfiguratorNodes || {}),
          productImageUrl: getProductImageUrlFromSelectNodes(item?.productInfo?.cTmCustomconfiguratorNodes || {}),
        }));
        setData(recordTTB || []);
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const getFilteredData = useCallback((filterValue: FilterTTBType) => {
    setFilterData(old => ({ ...old, ...filterValue }));
    setPage(1);
    setPageSize(PER_PAGE_DEFAULT);
  }, []);

  if (error) {
    message.error(error.message);
  }

  return (
    <>
      <Container>
        <Content>
          <TTBPageHeader />
          <TTBFilter options={filterData} getFilteredData={getFilteredData} isRoleTTBAgent={isRoleTTBAgent} />
          <TTBTable
            onRefresh={mutate}
            records={data}
            loading={loading || isValidating}
            isRoleAdmin={isRoleAdmin}
            getFilteredData={getFilteredData}
            isRoleTTBAgent={isRoleTTBAgent}
            isRoleTTBManager={isRoleTTBManager}
            pagination={{
              showSizeChanger: true,
              onChange: handleChangePage,
              pageSize,
              current: page,
              total,
              onShowSizeChange,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
            isSearching={debouncedSearch !== ''}
          />
        </Content>
      </Container>
    </>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }
`;
